import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import dataJson from "../assets/json/datos.json";
import NotFound from "../components/404";
import fondo from "../assets/img/fondoOferta.webp";

const GOOGLE_API_KEY = "AIzaSyBcIedFIBexXFCImTd3GMAEITM2092lOc0";

const fondos = require.context(
  "../assets/img",
  false,
  /\.(png|jpe?g|webp|svg)$/,
);

export default function Investigacion() {
  const { sede, seccion } = useParams();
  const navigate = useNavigate();

  const rightRef = useRef(null);
  const leftRef = useRef(null);

  const [fondoImg, setFondoImg] = useState(fondo);
  const [activeTab, setActiveTab] = useState(seccion);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const [driveFiles, setDriveFiles] = useState([]);
  const [loadingDrive, setLoadingDrive] = useState(false);

  const sectionTitles = {
    bienvenida: "Bienvenida",
    boletines: "Boletines",
    revistasalud: "Salud UNO",
    lineadeinvestigacion: "Línea de Investigación",
    investigaciones: "Investigaciones",
    docentesinvestigadores: "Docentes Investigadores",
  };

  const getTitle = (key) =>
    sectionTitles[key] || key?.charAt(0).toUpperCase() + key?.slice(1);

  const normalizeText = (t) =>
    t
      ?.normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveTab(seccion);
  }, [seccion]);

  const sedeKey = Object.keys(dataJson).find(
    (key) => normalizeText(key) === normalizeText(sede),
  );
  const sedeInfo = sedeKey ? dataJson[sedeKey] : null;
  const bloque = sedeInfo?.investigacion?.[0];

  useEffect(() => {
    if (sedeInfo?.imagenes?.fondoPrincipal) {
      try {
        setFondoImg(fondos(`./${sedeInfo.imagenes.fondoPrincipal}`));
      } catch {
        setFondoImg(fondo);
      }
    } else {
      setFondoImg(fondo);
    }
  }, [sedeInfo]);

  const extractFolderId = (urlOrId) => {
    if (!urlOrId) return null;
    const match = urlOrId.match(/folders\/([a-zA-Z0-9-_]+)/);
    return match ? match[1] : urlOrId;
  };

  useEffect(() => {
    if (seccion?.toLowerCase() === "boletines" && bloque?.boletines) {
      const folderId = extractFolderId(bloque.boletines);
      if (!folderId) return;

      setLoadingDrive(true);

      const url = `https://www.googleapis.com/drive/v3/files?q='${folderId}'+in+parents+and+trashed=false&fields=files(id,name,webViewLink,thumbnailLink)&orderBy=name+desc&key=${GOOGLE_API_KEY}`;
      fetch(url)
        .then((res) => res.json())
        .then((data) => {
          if (data.error) {
            console.error("Error de la API:", data.error.message);
            return;
          }

          if (data.files) {
            const formattedFiles = data.files.map((file) => ({
              titulo: file.name.replace(/\.[^/.]+$/, ""),
              url: file.webViewLink,
              img: file.thumbnailLink
                ? file.thumbnailLink.replace(/=s\d+/, "=w400")
                : "https://via.placeholder.com/400x500?text=Sin+Portada",
            }));
            setDriveFiles(formattedFiles);
          }
          setLoadingDrive(false);
        })
        .catch((err) => {
          console.error("Error crítico en la petición:", err);
          setLoadingDrive(false);
        });
    }
  }, [seccion, bloque]);

  if (!sedeInfo)
    return <h2 className="text-center mt-5">Sede no encontrada</h2>;
  if (!bloque)
    return (
      <h2 className="text-center mt-5">
        Esta sede no tiene sección de Investigación
      </h2>
    );

  const tabs = Object.keys(bloque).map((key) => ({
    key: key.toLowerCase(),
    label: getTitle(key.toLowerCase()),
  }));

  const seccionKey = seccion?.toLowerCase();
  let contenido;

  switch (seccionKey) {
    case "bienvenida":
      contenido = [{ contenido: bloque.bienvenida }];
      break;
    case "boletines":
      contenido = driveFiles;
      break;
    case "revistasalud":
      contenido = bloque.revistaSalud;
      break;
    case "lineadeinvestigacion":
      contenido = bloque.lineadeinvestigacion;
      break;
    case "investigaciones":
      contenido = bloque.investigaciones;
      break;
    case "docentesinvestigadores":
      contenido = bloque.docentesinvestigadores;
      break;
    default:
      contenido = null;
  }

  if (!contenido && seccionKey !== "boletines")
    return <NotFound mensaje={`No existe la sección "${seccion}"`} />;

  const handleTabClick = (k) => navigate(`/${sede}/investigacion/${k}`);

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
    exit: { opacity: 0, y: -50, transition: { duration: 0.4 } },
  };

  const tabVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.35 } },
    exit: { opacity: 0, y: -20, transition: { duration: 0.25 } },
  };

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        fontFamily: "'Montserrat', sans-serif",
        color: "#001A66",
        backgroundImage: `url(${fondoImg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
        marginTop: "-14rem",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(255,255,255,0.82)",
          zIndex: 0,
        }}
      />

      <AnimatePresence mode="wait">
        <motion.div
          key={seccion}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          style={{
            position: "relative",
            zIndex: 1,
            maxWidth: "1100px",
            margin: "0 auto",
            padding: "3rem 1rem",
            marginTop: "6rem",
          }}
        >
          <h1
            style={{
              fontSize: isMobile ? "2rem" : "3.5rem",
              fontWeight: 700,
              marginBottom: "2rem",
              textAlign: "center",
              marginTop: "6rem",
            }}
          >
            {getTitle(seccionKey)}
          </h1>

          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: "2rem",
              justifyContent: "center",
            }}
          >
            {/* IZQUIERDA */}
            <div ref={leftRef} style={{ flex: "1 1 100%", maxWidth: "600px" }}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={seccionKey}
                  variants={tabVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  {/* BIENVENIDA */}
                  {seccionKey === "bienvenida" &&
                    contenido.map((item, i) => (
                      <div
                        key={i}
                        style={{
                          background: "#fff",
                          padding: "1rem",
                          borderRadius: "10px",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                        }}
                      >
                        <p
                          style={{
                            whiteSpace: "pre-line",
                            textAlign: isMobile ? "center" : "left",
                          }}
                        >
                          {item.contenido}
                        </p>
                      </div>
                    ))}

                  {/* BOLETINES DINÁMICOS DESDE DRIVE */}
                  {seccionKey === "boletines" && (
                    <>
                      {loadingDrive ? (
                        <p className="text-center">
                          Cargando boletines oficiales...
                        </p>
                      ) : contenido.length === 0 ? (
                        <p className="text-center">
                          No se encontraron archivos en la carpeta especificada.
                        </p>
                      ) : (
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "repeat(auto-fit, minmax(160px, 1fr))",
                            gap: "1rem",
                          }}
                        >
                          {contenido.map((item, i) => (
                            <a
                              key={i}
                              href={item.url}
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                background: "#fff",
                                padding: "1rem",
                                borderRadius: "10px",
                                textDecoration: "none",
                                color: "#001A66",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                transition: "transform 0.2s ease",
                              }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.transform =
                                  "scale(1.03)")
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.transform = "scale(1)")
                              }
                            >
                              <img
                                src={item.img}
                                alt={item.titulo}
                                style={{
                                  width: "100%",
                                  height: "210px",
                                  objectFit: "cover",
                                  borderRadius: "8px",
                                  marginBottom: "0.5rem",
                                }}
                                referrerPolicy="no-referrer" // Previene problemas de bloqueo de imágenes de Google
                              />
                              <h4
                                style={{
                                  fontSize: "0.95rem",
                                  fontWeight: 700,
                                  lineHeight: "1.3",
                                }}
                              >
                                {item.titulo}
                              </h4>
                            </a>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  {/* REVISTA SALUD */}
                  {seccionKey === "revistasalud" && (
                    <div
                      style={{
                        background: "#fff",
                        padding: "1.5rem",
                        borderRadius: "10px",
                        textAlign: "center",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                      }}
                    >
                      <p
                        style={{
                          marginBottom: "1rem",
                          whiteSpace: "pre-line",
                          lineHeight: "1.5",
                        }}
                      >
                        La Revista Salud UNO es un espacio académico dedicado a
                        la difusión de investigaciones...
                      </p>
                      <button
                        onClick={() => window.open(contenido, "_blank")}
                        style={{
                          background: "#001A66",
                          color: "#fff",
                          border: "none",
                          padding: "0.7rem 1.2rem",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontWeight: 600,
                        }}
                      >
                        Ver Revista Salud UNO
                      </button>
                    </div>
                  )}

                  {/* LINEAS DE INVESTIGACION */}
                  {seccionKey === "lineadeinvestigacion" && (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                        gap: "1rem",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {contenido.map((item, i) => (
                        <div
                          key={i}
                          style={{
                            background: "#fff",
                            padding: "1.5rem",
                            borderRadius: "10px",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            textAlign: "center",
                            minHeight: "100px",
                          }}
                        >
                          <h5 style={{ fontWeight: 700, margin: 0 }}>
                            {item.titulo}
                          </h5>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* INVESTIGACIONES */}
                  {seccionKey === "investigaciones" &&
                    contenido.map((item, i) => (
                      <a
                        key={i}
                        href={item.url}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          display: "block",
                          background: "#fff",
                          padding: "1rem",
                          borderRadius: "8px",
                          textDecoration: "none",
                          color: "#001A66",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                          marginBottom: "1rem",
                        }}
                      >
                        <h5
                          style={{
                            fontWeight: 700,
                            textAlign: isMobile ? "center" : "left",
                          }}
                        >
                          {item.titulo}
                        </h5>
                        <p
                          style={{
                            whiteSpace: "pre-line",
                            textAlign: isMobile ? "center" : "left",
                          }}
                        >
                          {item.descripcion}
                        </p>
                      </a>
                    ))}

                  {/* DOCENTES */}
                  {seccionKey === "docentesinvestigadores" &&
                    contenido.map((item, i) => (
                      <div
                        key={i}
                        style={{
                          background: "#fff",
                          padding: "1rem",
                          borderRadius: "8px",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                          marginBottom: "1rem",
                          textAlign: isMobile ? "center" : "left",
                        }}
                      >
                        {item.docente}
                      </div>
                    ))}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* DERECHA */}
            <div
              style={{
                flex: "0 0 380px",
                maxWidth: isMobile ? "100%" : "380px",
              }}
            >
              <div
                ref={rightRef}
                style={{
                  position: isMobile ? "relative" : "sticky",
                  top: "20px",
                }}
              >
                <div
                  style={{
                    backgroundColor: "#001A66",
                    color: "#fff",
                    padding: "0.5rem 1rem",
                    fontWeight: 700,
                    marginBottom: "1.5rem",
                    borderRadius: "5px",
                    textAlign: "center",
                    borderBottom: "5px solid #009DFA",
                  }}
                >
                  Investigación UNO
                </div>
                <div
                  style={{
                    background: "#F5F7FF",
                    padding: "1rem",
                    borderRadius: "8px",
                    boxShadow: "0 0 6px rgba(0,0,0,0.1)",
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem",
                  }}
                >
                  {tabs.map((t) => (
                    <button
                      key={t.key}
                      onClick={() => handleTabClick(t.key)}
                      style={{
                        backgroundColor:
                          activeTab === t.key ? "#009DFA" : "#001A66",
                        color: "#fff",
                        border: "none",
                        padding: "0.6rem",
                        cursor: "pointer",
                        fontWeight: 600,
                        borderRadius: "5px",
                      }}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
